import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { Handle, InsertHandleBody, UpdateHandleBody } from "./types/handles";

export const useHandlesApi = () => {
  const api = useApi();

  const useAdminHandles = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/vertigrip/handles", query);
      return useSWR<Paginated<Handle>, CustomError, string>(url, (url) => api.get<Paginated<Handle>>({ url, signal }));
    },
    [api],
  );

  const insertAdminHandle = useCallback(
    async (body: InsertHandleBody): Promise<{ id: string }> => {
      const url = `/admin/settings/vertigrip/handles`;
      const id = await api.post<{ id: string }, InsertHandleBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminHandle = useCallback(
    async (handleId: string, body: UpdateHandleBody): Promise<void> => {
      const url = `/admin/settings/vertigrip/handles/${handleId}`;
      (await api.put<UpdateHandleBody>(url, body), mutateCache([`/admin/settings/vertigrip/handles`]));
    },
    [api],
  );

  const deleteAdminHandle = useCallback(
    async (handleId: string): Promise<void> => {
      await api.del(`/admin/settings/vertigrip/handles/${handleId}`);
      mutateCache([`/admin/settings/vertigrip/handles`]);
    },
    [api],
  );

  const updateAdminHandleVisibility = useCallback(
    async (handleId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/vertigrip/handles/${handleId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/vertigrip/handles"]);
    },
    [api],
  );

  return {
    useAdminHandles,
    insertAdminHandle,
    updateAdminHandle,
    updateAdminHandleVisibility,
    deleteAdminHandle,
  };
};
